import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

export interface Reference {
  authors: string;
  year: string;
  title: string;
  source?: string;
}

/** The music-emotion research emotify's methodology draws on. Shared by the
 *  About and Methodology pages so the citation list stays consistent. */
export const MUSIC_EMOTION_REFERENCES: Reference[] = [
  {
    authors: 'Russell, J. A.',
    year: '1980',
    title: 'A circumplex model of affect',
    source: 'Journal of Personality and Social Psychology, 39(6), 1161–1178',
  },
  {
    authors: 'Thayer, R. E.',
    year: '1989',
    title: 'The Biopsychology of Mood and Arousal',
    source: 'Oxford University Press',
  },
  {
    authors: 'Gabrielsson, A., & Lindström, E.',
    year: '2001',
    title: 'The influence of musical structure on emotional expression',
    source: 'In Music and Emotion: Theory and Research (pp. 223–248). Oxford University Press',
  },
  {
    authors: 'Eerola, T., & Vuoskoski, J. K.',
    year: '2011',
    title: 'A comparison of the discrete and dimensional models of emotion in music',
    source: 'Psychology of Music, 39(1), 18–49',
  },
  {
    authors: 'Thoma, M. V., et al.',
    year: '2013',
    title: 'The effect of music on the human stress response',
    source: 'PLoS ONE, 8(8), e70156',
  },
];

interface ReferencesProps {
  references?: Reference[];
  title?: string;
}

const References: React.FC<ReferencesProps> = ({
  references = MUSIC_EMOTION_REFERENCES,
  title = 'References',
}) => {
  const theme = useTheme();

  return (
    <Box component="section" aria-label="References">
      <Typography
        variant="h5"
        sx={{ fontSize: '20px', fontWeight: 700, mb: 2.5, color: theme.palette.text.primary }}
      >
        {title}
      </Typography>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {references.map((ref, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1.75rem 1fr',
              alignItems: 'baseline',
              fontSize: '14px',
              lineHeight: 1.6,
              color: theme.palette.text.secondary,
            }}
          >
            <Typography
              component="span"
              aria-hidden="true"
              sx={{ fontSize: '13px', fontWeight: 700, color: theme.palette.text.disabled, fontVariantNumeric: 'tabular-nums' }}
            >
              {i + 1}.
            </Typography>
            <Typography component="span" sx={{ fontSize: '14px', lineHeight: 1.6, color: theme.palette.text.secondary }}>
              <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>{ref.authors}</Box>
              {' '}({ref.year}). <Box component="em" sx={{ fontStyle: 'italic' }}>{ref.title}</Box>.
              {ref.source && <>{' '}{ref.source}.</>}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default References;
